import { useEffect, useState } from "react";
import { KeyRound, Loader2, MailCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { forgotPassword } from "@/lib/boide-api";
import { errorName, trackEvent } from "@/lib/analytics";

/** Khớp `RESEND_COOLDOWN_SECONDS` ở BE (`src/utils/constant/auth.py`). */
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * "Quên mật khẩu" — gửi mail đặt lại.
 *
 * 🔴 MÀN THÀNH CÔNG PHẢI NÓI NƯỚC ĐÔI ("nếu tài khoản tồn tại..."), vì BE cố ý trả
 * y hệt nhau cho email có thật và email không có (`ctr_forgot_password` — chống dò
 * tài khoản). Viết "Đã gửi mail tới abc@x.com" là biến màn này thành công cụ kiểm
 * tra email nào đã đăng ký, đúng thứ BE vừa từ chối tiết lộ.
 */
export function ForgotPasswordDialog({
  open,
  onOpenChange,
  defaultUsername = "",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Điền sẵn từ ô đăng nhập — người dùng vừa gõ email ở đó rồi. */
  defaultUsername?: string;
}) {
  const [username, setUsername] = useState(defaultUsername);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Mở lại là một lượt mới: giữ nguyên màn "đã gửi" của lần trước sẽ khiến người
  // dùng tưởng vừa có mail mới trong khi chưa gửi gì.
  useEffect(() => {
    if (!open) return;
    setUsername(defaultUsername);
    setSent(false);
    setCooldown(0);
  }, [open, defaultUsername]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = async () => {
    const value = username.trim();
    if (!value) {
      toast.error("Vui lòng nhập email hoặc tên đăng nhập.");
      return;
    }
    setSending(true);
    try {
      await forgotPassword(value);
      trackEvent("auth_forgot_password_sent");
      setSent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      trackEvent("auth_forgot_password_failed", { error_name: errorName(err) });
      toast.error(
        err instanceof Error ? err.message : "Không gửi được yêu cầu, vui lòng thử lại sau.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (sending ? null : onOpenChange(v))}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display tracking-[0.12em]">
            <KeyRound className="size-5 text-primary" />
            QUÊN MẬT KHẨU
          </DialogTitle>
          <DialogDescription className="font-serif-soft italic">
            Nhập email (hoặc tên đăng nhập) của bạn, chúng tôi sẽ gửi link đặt lại mật khẩu.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-3">
            <div className="flex gap-3 rounded-lg border border-gold-soft/60 bg-background/40 p-4">
              <MailCheck className="mt-0.5 size-5 shrink-0 text-primary" />
              <div className="space-y-1.5 text-sm">
                <p className="font-semibold text-foreground">Đã gửi yêu cầu</p>
                <p className="text-muted-foreground">
                  Nếu tài khoản tồn tại, link đặt lại mật khẩu đã được gửi tới email của tài khoản
                  đó. Link có hiệu lực trong khoảng 1 giờ.
                </p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Không thấy mail? Kiểm tra mục Spam / Quảng cáo. Mỗi lần bấm lại sẽ vô hiệu hoá link
              cũ, nên hãy dùng mail mới nhất.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="forgot-username" className="text-[11px] uppercase tracking-wider">
              Email hoặc tên đăng nhập
            </Label>
            <Input
              id="forgot-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void send();
              }}
              autoComplete="username"
              placeholder="ban@email.com"
              disabled={sending}
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground">
              Tài khoản đăng nhập bằng Google/Apple mà chưa từng đặt mật khẩu sẽ không nhận được
              mail — hãy đăng nhập bằng nút Google/Apple như bình thường.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" disabled={sending} onClick={() => onOpenChange(false)}>
            {sent ? "Đóng" : "Huỷ"}
          </Button>
          <Button onClick={() => void send()} disabled={sending || cooldown > 0}>
            {sending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            {sending
              ? "Đang gửi..."
              : cooldown > 0
                ? `Gửi lại sau ${cooldown}s`
                : sent
                  ? "Gửi lại"
                  : "Gửi link đặt lại"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
